import * as Phaser from "phaser-ce"
import { log } from "../../../common/Logging"
import { FontType } from "../../../common/TextUtils"
import { ActivityKey } from "../../../shared/models/ActivityKey"
import {
    ButtonContainer,
    Buttons,
    ButtonSize,
    ButtonType,
} from "../../generic-sprites/Buttons"
import { DebugPanel } from "../../generic-sprites/DebugPanel"
import { ExpandoSprite } from "../../generic-sprites/ExpandoSprite"
import { loadGenericAssets } from "../../generic-sprites/GenericAssets"
import { colorNum, ColorUtils } from "../../utils/ColorUtils"
import { PhaserText } from "../../utils/PhaserText"
import { PhaserUtils } from "../../utils/PhaserUtils"
import { Scale } from "../../utils/Scale"
import { Sprites } from "../../utils/Sprites"
import { Videos } from "../../utils/Videos"
import { Transitions } from "../transitions/Transitions"
import { ArrowButton } from "./sprites/ArrowButton"
import { DashboardButton } from "./sprites/DashboardButton"
import { EffectsWindow, OrganType } from "./sprites/EffectsWindow"
import {
    loadAssets,
    TimeMachineColor,
    TmAudio,
    TmImage,
    TmSprite,
    TmVideo,
} from "./TimeMachineAssets"
import { NicName, NicType, TmType, tmTypes } from "./TmType"

const tmFont = { type: FontType.FUTURISTIC, color: TimeMachineColor.LIGHT }

export class TimeMachineState extends Phaser.State {
    static readonly SOUND_EFFECT_VOLUME = 0.4

    background: Phaser.Sprite
    nicTypeGroup: Phaser.Group
    debugPanel: DebugPanel
    xrayButton: DashboardButton
    replayButton: DashboardButton
    selectButtons: Phaser.Group
    typeText: Phaser.Text
    nextArrow: ArrowButton
    prevArrow: ArrowButton
    yearsBar: Phaser.Sprite
    effects: EffectsWindow
    organsClicked: Set<OrganType>
    nicTypesCompleted: Set<NicType> = new Set()
    replayAudio = false
    doneAudio = false
    reviewButtons: Phaser.Group
    tmTypes

    preload() {
        loadGenericAssets(this.game)
        loadAssets(this.game)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)
        ColorUtils.setBackgroundColorBlack(this.game)

        const group = this.game.add.group()
        this.background = Sprites.add(this.game, TmImage.BACKGROUND)
        if (this.debugPanel) {
            this.debugPanel.recreate()
        } else {
            this.debugPanel = new DebugPanel(this.game)
        }

        const powerUp = this.game.add.audio(TmAudio.POWER_UP)
        powerUp.volume = TimeMachineState.SOUND_EFFECT_VOLUME
        const matrix = await Videos.add(this.game, TmVideo.MATRIX_VIDEO, {
            group,
            scale: 0.75,
            onComplete: () => {
                matrix.destroy()
                this.background.destroy()
                this.setupSelectNicType()
            },
            onPlay: () => powerUp.play(),
        })
        this.debugPanel.setSkip(() => matrix.video.complete())
    }

    setupSelectNicType = () => {
        if (this.nicTypeGroup) {
            this.nicTypeGroup.destroy()
        }

        this.background = Sprites.add(this.game, TmImage.BACKGROUND)

        if (this.debugPanel) {
            this.debugPanel.recreate()
        } else {
            this.debugPanel = new DebugPanel(this.game)
        }

        const selectedAudio = this.selectAudioType()
        const audio = this.game.add.audio(selectedAudio)

        if (selectedAudio === TmAudio.ONE) {
            this.debugPanel.setSkip(() => audio.stop())
            audio.onPlay.add(() => this.disableButtonGroup(this.selectButtons))
            audio.onStop.add(() => this.enableButtonGroup(this.selectButtons))
        }

        this.selectButtons = this.game.add.group()
        const yScale = 0.2
        this.tmTypes = tmTypes()
        const chewerButton = this.viewTypeButton(
            this.tmTypes.chewer,
            Scale.y(yScale),
            audio,
        )
        const buttonSpacing = chewerButton.button.height + Scale.y(0.05)
        this.viewTypeButton(
            this.tmTypes.smoker,
            chewerButton.button.y + buttonSpacing,
            audio,
        )
        this.viewTypeButton(
            this.tmTypes.vaper,
            chewerButton.button.y + buttonSpacing * 2,
            audio,
        )

        audio.play()
    }

    disableButtonGroup = (group: Phaser.Group) => {
        group.forEach((child) => {
            if (child.type === 1) {
                const button = child as Phaser.Button
                button.tint = colorNum.gray
                button.inputEnabled = false
            }
            if (child.type === 4) {
                const text = child as Phaser.Text
                text.tint = colorNum.gray
            }
        })
    }

    enableButtonGroup = (group: Phaser.Group) => {
        group.forEach((child) => {
            if (child.type === 1) {
                const button = child as Phaser.Button
                button.tint = colorNum.white
                button.inputEnabled = true
                button.input.useHandCursor = true
            }
            if (child.type === 4) {
                const text = child as Phaser.Text
                text.tint = colorNum.white
            }
        })
    }

    exitSequence = () => {
        const group = this.game.add.group()
        this.background.destroy()
        this.reviewButtons.destroy()
        this.nicTypeGroup.destroy()
        this.background = Sprites.add(this.game, TmImage.BACKGROUND)
        const powerDown = this.game.add.audio(TmAudio.POWER_DOWN)
        powerDown.volume = TimeMachineState.SOUND_EFFECT_VOLUME
        let videoRef
        powerDown.onPlay.addOnce(async () => {
            videoRef = await Videos.add(this.game, TmVideo.MATRIX_VIDEO, {
                group,
                scale: 0.5,
            })
        })
        powerDown.onStop.addOnce(() => {
            videoRef.destroy()
            Transitions.startOutro(this.game, ActivityKey.TIME_MACHINE)
        })
        powerDown.play()
    }

    exitButton = (y: number) =>
        Buttons.create(this.game, "Next", () => this.exitSequence(), {
            x: Scale.x(0.65),
            y,
            scale: 1,
            group: this.reviewButtons,
            buttonType: ButtonType.GHOST,
            buttonSize: ButtonSize.WIDE,
            textConfig: {
                type: FontType.FUTURISTIC,
                fontPx: 18,
            },
            enabled: true,
        })

    reviewTypeButton = (type: TmType, y: number): ButtonContainer =>
        Buttons.create(
            this.game,
            `Switch to ${type.name}`,
            () => this.setupReview(type),
            {
                x: Scale.x(0.65),
                y,
                scale: 1,
                group: this.reviewButtons,
                buttonType: ButtonType.GHOST,
                buttonSize: ButtonSize.WIDE,
                textConfig: {
                    type: FontType.FUTURISTIC,
                    fontPx: 14,
                },
                enabled: true,
            },
        )

    showReviewButtons = (type: TmType) => {
        this.reviewButtons = this.game.add.group()
        switch (type.nicName) {
            case NicName.SMOKING:
                this.setReviewButtons(this.tmTypes.chewer, this.tmTypes.vaper)
                break
            case NicName.VAPING:
                this.setReviewButtons(this.tmTypes.chewer, this.tmTypes.smoker)
                break
            case NicName.CHEW:
                this.setReviewButtons(this.tmTypes.vaper, this.tmTypes.smoker)
                break
            default:
                throw new Error("Unexpected review button type.")
        }
    }

    setReviewButtons = (t1: TmType, t2: TmType) => {
        const button1 = this.reviewTypeButton(t1, Scale.y(0.25))
        const buttonSpacing = button1.button.height + Scale.y(0.05)
        this.reviewTypeButton(t2, button1.button.y + buttonSpacing)
        this.exitButton(button1.button.y + buttonSpacing * 2)
    }

    viewTypeButton = (
        type: TmType,
        y: number,
        audio: Phaser.Sound,
    ): ButtonContainer => {
        return Buttons.create(
            this.game,
            type.name,
            () => {
                audio.stop()
                this.setupVideo(type)
            },
            {
                centerInWorldX: true,
                y,
                scale: 1,
                group: this.selectButtons,
                buttonType: ButtonType.GHOST,
                buttonSize: ButtonSize.WIDE,
                textConfig: { type: FontType.FUTURISTIC },
                enabled: !this.nicTypesCompleted.has(type.name),
            },
        )
    }

    nicTypeCompete = (nicType: NicType) => {
        this.nicTypesCompleted.add(nicType)
        if (this.nicTypesCompleted.size === 3) {
            switch (nicType) {
                case NicType.SMOKER:
                    this.setupReview(this.tmTypes.smoker)
                    break
                case NicType.CHEWER:
                    this.setupReview(this.tmTypes.chewer)
                    break
                case NicType.VAPER:
                    this.setupReview(this.tmTypes.vaper)
                    break
                default:
                    throw new Error("Unexpected nic type in nic type complete.")
            }
        } else {
            this.setupSelectNicType()
        }
    }

    setOrgansOnDisplay = (type: TmType, group: Phaser.Group) => {
        this.nicTypeGroup = group

        const body = Sprites.add(this.game, TmImage.EMPTY_BODY, {
            group,
            x: Scale.x(0.19),
            y: Scale.y(0.23),
        })

        let heartOrgan
        return type.organs.map((organ) => {
            const organSprite = new ExpandoSprite(
                this.game,
                organ.x,
                organ.y,
                organ.asset,
                { group, on: false, ignoreCoors: heartOrgan },
            )
            if (organ.organType === OrganType.HEART) {
                heartOrgan = organSprite
            }
            if (organ.centerX) {
                Sprites.centerSpriteInSprite(body, organSprite)
            }
            organSprite.y = organ.y
            organSprite.addTitle(organ.asset.replace(".png", ""), {
                fontType: FontType.FUTURISTIC,
                firstCharCaps: true,
            })
            return { organ, organSprite }
        })
    }

    setupReview = (type: TmType) => {
        if (this.nicTypeGroup) {
            this.nicTypeGroup.destroy()
        }

        const group = this.game.add.group()
        const organs = this.setOrgansOnDisplay(type, group)
        organs.forEach((organ) => {
            const onOrganClick = () => {
                this.reviewButtons.destroy()
                organ.organSprite.unexpand()
                organs.forEach((turnOffOrgan) =>
                    turnOffOrgan.organSprite.turnOff(),
                )
                this.organsClicked.add(organ.organ.organType)
                this.effects = new EffectsWindow(
                    this.game,
                    type.nicName,
                    organ.organ.organType,
                    organ.organ.textBottom,
                    () => {
                        organs.forEach((turnOnOrgan) =>
                            turnOnOrgan.organSprite.turnOn(),
                        )
                        if (this.prevArrow) {
                            this.prevArrow.disable()
                        }

                        if (this.nextArrow) {
                            this.nextArrow.disable()
                        }

                        if (this.prevArrow) {
                            this.replayButton.undoButton()
                        }

                        this.showReviewButtons(type)
                        this.yearsBar.frame = 0
                    },
                )
                this.effects.closeButton.enable()
                this.effects.closeButton.addOnClick(this.effects.close)
                this.effects.incYearNum(this.changedYearNumber)
                this.debugPanel.setSkip(this.nextArrowClicked, false)
            }

            organ.organSprite.turnOn()
            organ.organSprite.addOnClick(onOrganClick)
        })

        if (this.reviewButtons) {
            this.reviewButtons.destroy()
        }

        if (!this.doneAudio) {
            log.debug("done audio not played")
            this.showReviewButtons(type)
            const doneAudio = this.game.add.audio(this.effects.nicDoneAudio())
            doneAudio.onPlay.add(() => {
                organs.forEach((organ) => {
                    this.disableButtonGroup(this.reviewButtons)
                    organ.organSprite.turnOff()
                })
            })
            doneAudio.onStop.add(() => {
                this.enableButtonGroup(this.reviewButtons)
                organs.forEach((organ) => organ.organSprite.turnOn())
            })
            doneAudio.play()
            this.doneAudio = true
        } else {
            this.showReviewButtons(type)
        }

        this.setupDashboard(type, group)
        this.debugPanel.recreate()
    }

    setupVideo = async (type: TmType) => {
        this.organsClicked = new Set()
        this.selectButtons.destroy()

        const group = this.game.add.group()
        const organs = this.setOrgansOnDisplay(type, group)

        const vidY = Scale.y(0.23)
        const scanAnim = "scanAnim"
        const scan = Sprites.add(this.game, type.scan, {
            group,
            x: type.scanX,
            y: type.scanY,
        })
        scan.animations.add(scanAnim, undefined, 14)

        const clickOtherBody = this.game.add.audio(`time-machine-n3.mp3`)
        const clickOrganAudioDone = () => {
            video.destroy()
            scan.destroy()
            organs.forEach((organ) => {
                const onOrganClick = () => {
                    organ.organSprite.unexpand()
                    organs.forEach((organToTurnOff) =>
                        organToTurnOff.organSprite.turnOff(),
                    )
                    clickOtherBody.stop()
                    this.organsClicked.add(organ.organ.organType)
                    this.effects = new EffectsWindow(
                        this.game,
                        type.nicName,
                        organ.organ.organType,
                        organ.organ.textBottom,
                        () => {
                            organs.forEach((organToTurnOn) =>
                                organToTurnOn.organSprite.turnOn(),
                            )
                            this.yearsBar.frame = 0
                            if (this.organsClicked.size < 3) {
                                clickOtherBody.play()
                                this.nextArrow.disable()
                                this.prevArrow.disable()
                                this.replayButton.undoButton()
                            } else {
                                organs.forEach((organToTurnOff) =>
                                    organToTurnOff.organSprite.turnOff(),
                                )
                                const outroAudio = this.game.add.audio(
                                    this.effects.nicOutroAudio(),
                                )
                                outroAudio.onStop.add(() =>
                                    this.nicTypeCompete(type.name),
                                )
                                outroAudio.play()
                                this.nextArrow.disable()
                                this.prevArrow.disable()
                                this.replayButton.undoButton()
                                this.debugPanel.setSkip(() => outroAudio.stop())
                            }
                            organ.organSprite.grayOut()
                        },
                    )
                    this.effects.incYearNum(this.changedYearNumber)
                    this.debugPanel.setSkip(this.nextArrowClicked, false)
                }

                organ.organSprite.turnOn()
                organ.organSprite.addOnClick(onOrganClick)
            })
        }

        this.debugPanel.setSkip(clickOrganAudioDone)

        const video = await Videos.add(this.game, type.video, {
            group,
            x: type.videoX,
            y: vidY,
            onComplete: () => {
                video.destroy()
                const scanAudio = this.game.add.audio(
                    `time-machine-${type.nicName}-n1.mp3`,
                )
                scanAudio.play()
                scanAudio.onStop.addOnce(() => {
                    this.xrayButton.onClick(() => {
                        scan.animations.play(scanAnim)
                        scan.animations.currentAnim.onComplete.addOnce(() => {
                            const clickOrganAudio = this.game.add.audio(
                                `time-machine-${type.nicName}-n2.mp3`,
                            )
                            clickOrganAudio.play()
                            clickOrganAudio.onStop.addOnce(clickOrganAudioDone)
                        })
                        this.game.add.audio(TmAudio.SCAN_SOUND).play()
                        this.xrayButton.undoButton()
                    })
                })
            },
        })

        this.setupDashboard(type, group)
        this.debugPanel.recreate()
    }

    setupDashboard = (type: TmType, group: Phaser.Group) => {
        const scale = 0.8
        this.background.destroy()
        this.background = Sprites.add(this.game, TmImage.VIDEO_BACKGROUND, {
            group,
        })
        Scale.fillScreen(this.background)

        const buttonY = Scale.y(0.91)
        this.xrayButton = new DashboardButton(
            this.game,
            "X-RAY",
            Scale.x(0.3),
            buttonY,
            scale,
            true,
            group,
        )
        // A bit funky because of how we flipped the button
        const rightButtonX = Scale.width() - this.xrayButton.button.x
        this.replayButton = new DashboardButton(
            this.game,
            "REPLAY",
            rightButtonX,
            buttonY,
            scale,
            false,
            group,
        )

        this.typeText = PhaserText.create(this.game, type.name, {
            group,
            type: FontType.FUTURISTIC,
            fontPx: 32,
            y: Scale.y(0.135),
        })
        Sprites.centerInWorldXOnly(this.typeText)

        this.nextArrow = new ArrowButton(this.game, 0, buttonY, group)
        Sprites.toLeftOf(this.nextArrow.button, this.replayButton.button, 0.02)

        this.prevArrow = new ArrowButton(this.game, 0, buttonY, group, true)
        this.prevArrow.button.x = Sprites.toRightOf(
            this.xrayButton.button,
            0.23,
        )

        this.yearsBar = Sprites.add(this.game, TmSprite.TIME_BAR, {
            group,
            scale: 0.6,
        })
        Sprites.centerSpriteInSprite(this.nextArrow.button, this.yearsBar)
        Sprites.centerInWorldXOnly(this.yearsBar)

        const years = PhaserText.create(this.game, "years", {
            group,
            y: this.yearsBar.y + this.yearsBar.height - Scale.y(0.01),
            ...tmFont,
        })
        Sprites.centerInWorldXOnly(years)

        Sprites.evenlySpaceHorizontallyBetween(
            Scale.x(0.402),
            Scale.x(0.597),
            ...["0", "1", "5", "10"].map((it) =>
                PhaserText.create(this.game, it, {
                    y: this.yearsBar.y - Scale.y(0.05),
                    ...tmFont,
                }),
            ),
        )
    }

    nextArrowClicked = () => {
        if (this.effects.yearNum > 2) {
            this.debugPanel.disableSkip()
            this.effects.audioClip.stop()
            this.effects.closeButton.enable()
            this.effects.closeButton.addOnClick(this.effects.close)
        } else {
            ;(this.yearsBar.frame as number)++
            this.nextArrow.beep()
            this.effects.incYearNum(this.changedYearNumber)
            this.nextArrow.disable()
            this.replayButton.undoButton()
            if (this.effects.yearNum > 1) {
                this.prevArrow.disable()
            }
        }
    }

    prevArrowClicked = () => {
        if (this.effects.yearNum >= 1) {
            ;(this.yearsBar.frame as number)--
            this.effects.decYearNum(this.changedYearNumber)
            this.nextArrow.disable()
            this.prevArrow.disable()
            this.replayButton.undoButton()
        }
    }

    changedYearNumber = () => {
        const year = this.effects.yearNum
        this.nextArrow.addOnClick(this.nextArrowClicked)
        this.replayButton.onClick(this.onReplayButtonPress)

        if (year === 0 && !this.replayAudio) {
            this.replayAudio = true
            const replayAudio = this.game.add.audio(TmAudio.TWO)
            replayAudio.onPlay.add(() => {
                this.nextArrow.disable()
                this.replayButton.undoButton()
                this.replayButton.active()
            })
            replayAudio.onStop.add(() => {
                this.nextArrow.addOnClick(this.nextArrowClicked)
                this.replayButton.onClick(this.onReplayButtonPress)
            })
            replayAudio.play()
        }

        if (year >= 1 && year <= 3) {
            this.prevArrow.active()
            this.prevArrow.addOnClick(this.prevArrowClicked)
        }

        if (year < 3) {
            this.nextArrow.flash()
        } else {
            this.nextArrow.disable()
            this.debugPanel.disableSkip()
            this.effects.closeButton.enable()
            this.effects.closeButton.addOnClick(this.effects.close)
        }
    }

    private selectAudioType = (): TmAudio => {
        if (this.nicTypesCompleted.size === 0) {
            return TmAudio.ONE
        } else if (this.nicTypesCompleted.size === 1) {
            if (this.nicTypesCompleted.has(NicType.CHEWER)) {
                return TmAudio.SMOKING_VAPING
            } else if (this.nicTypesCompleted.has(NicType.SMOKER)) {
                return TmAudio.CHEWING_VAPING
            } else {
                return TmAudio.CHEWING_SMOKING
            }
        } else {
            if (!this.nicTypesCompleted.has(NicType.CHEWER)) {
                return TmAudio.CHEWING_OUTRO
            } else if (!this.nicTypesCompleted.has(NicType.SMOKER)) {
                return TmAudio.SMOKING_OUTRO
            } else {
                return TmAudio.VAPING_OUTRO
            }
        }
    }

    private onReplayButtonPress = () => {
        const year = this.effects.yearNum
        if (this.effects.audioClip) {
            this.effects.audioClip.stop()
        }

        this.effects.audioClip.onPlay.add(() => {
            if (year >= 1 && year <= 3) {
                this.prevArrow.disable()
                this.prevArrow.active()
            }

            if (year < 3) {
                this.nextArrow.disable()
                this.nextArrow.flash()
            }

            this.replayButton.pressed()
        })
        this.effects.audioClip.onStop.add(() => {
            if (year >= 1 && year <= 3) {
                this.prevArrow.addOnClick(this.prevArrowClicked)
            }

            if (year < 3) {
                this.nextArrow.addOnClick(this.nextArrowClicked)
            }

            this.replayButton.onClick(this.onReplayButtonPress)
        })
        this.effects.audioClip.play()
    }
}
